// Botón «Modelo» + modal con los parámetros del SCI (el botón de la topbar del mock). Los
// parámetros se consultan sin salir del explorer de evaluaciones.
import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useScoringParameters } from '../api/useModeliaEvaluations';
import { resolveReadState } from '../logic';
import { ModeliaModelTab } from './ModeliaModelTab';
import { ModeliaForbiddenBlock, ModeliaReadStateBlock } from './ModeliaReadState';

const NO_PARAMETERS_PERMISSION =
    'Ver los parámetros del modelo requiere el permiso loan.parameter.read (GET /public/parameters). Pídeselo a tu administrador para tu rol: la lista de evaluaciones sigue funcionando sin él.';

export interface ModeliaModelDialogProps {
    canViewParameters: boolean;
}

export function ModeliaModelDialog({ canViewParameters }: ModeliaModelDialogProps) {
    const [open, setOpen] = useState(false);
    // Cerrado no se pide nada: los parámetros se leen cuando alguien abre el diálogo.
    const parameters = useScoringParameters(open && canViewParameters);
    const state = resolveReadState({
        isLoading: parameters.isLoading,
        error: parameters.error,
        count: parameters.groups.length,
    });

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-6 flex-none gap-1.5 px-2 text-[11px] text-muted-foreground"
                title="Los parámetros con los que el motor calcula el SCI"
                onClick={() => {
                    setOpen(true);
                }}
            >
                <SlidersHorizontal aria-hidden />
                Modelo
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-3xl grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="text-base">Modelo · parámetros del SCI</DialogTitle>
                        <DialogDescription className="text-[11.5px]">
                            Con qué valores calcula el motor el score, en solo lectura.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="-mx-4 flex min-h-0 flex-col overflow-hidden border-y border-border sm:-mx-6">
                        {!canViewParameters ? (
                            <ModeliaForbiddenBlock
                                description={NO_PARAMETERS_PERMISSION}
                                className="m-4"
                            />
                        ) : state === 'ready' ? (
                            <ModeliaModelTab groups={parameters.groups} />
                        ) : (
                            <ModeliaReadStateBlock
                                state={state}
                                loadingText="Cargando los parámetros del modelo…"
                                emptyTitle="El motor no devolvió parámetros de scoring"
                                emptyDescription="La categoría SCORING llegó vacía: no hay valores que mostrar."
                                errorTitle="No se pudieron leer los parámetros del modelo"
                                error={parameters.error}
                                onRetry={parameters.refetch}
                                forbiddenDescription={NO_PARAMETERS_PERMISSION}
                                className="m-4"
                            />
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
